import { unpackFlight } from "./wire";
import type { ClientHost } from "../../client/mode";
import {
  ApiError,
  requestId,
  type Pointer,
  type PlatformSocket,
} from "../../client/platform";
import { CanvasView } from "../../client/canvas-view";
import {
  renderDrawingTools,
  renderBrushCatalog,
  type DrawingToolState,
} from "../../client/drawing-tools";
import {
  DrawingEditor,
  emptyDrawing,
  renderTransparentDrawing,
  STROKE_DEFAULT_WIDTH,
  DRAG_BRUSHES,
} from "../../shared/art/drawing";
import type { StrokeBrush, StrokeShape } from "../../shared/art/types";
import { createFlight, flightAction, stepFlight, worldWidth } from "./engine";
import { worldDragDelta } from "./viewport";
import {
  MAIN_WEAPONS,
  EVOLUTIONS,
  EVENT_NAMES,
  weaponInfo,
  upgradeDescription,
  xpRequired,
  SIDE_WEAPONS,
  SCENES,
  BOSSES,
  starsFor,
  upgradeInfo,
} from "./content";
import {
  type FlightDesign,
  type FlightProfile,
  type FlightRoom,
  type FlightRoomSummary,
  type FlightState,
  type FlightAction,
  type RunSave,
  type MainWeapon,
} from "./types";
import { FlightStorage, emptyProfile } from "./storage";
import { makeSprite, drawSprite, paintFlight, setArtCanvasFactory, type Sprite } from "./render";
import { FONT, INK, NUM_FONT, box, disc, glow, linear, radial, rgba, shade } from "./art/kit";
import { icon, upgradeGlyph } from "./art/icons";
import {
  EVENT_COLOR,
  EVENT_GLYPH,
  GROUP_COLORS,
  chip,
  dim,
  fitText,
  glass,
  label,
  medallion,
  meter,
  numberText,
  outlined,
  paper,
  ribbon,
  roundButton,
  statTiles,
  stickerButton,
  sweep,
  blueprint,
  pad,
  type Tone,
} from "./art/hud";

const ROOT = "/modes/doodle-flight",
  copy = <T>(v: T): T => JSON.parse(JSON.stringify(v));
export class FlightClient implements DrawingToolState {
  color = "#000000";
  eraser = false;
  width = 12;
  eraserWidth = 26;
  brush: StrokeBrush = "pen";
  brushWidths = { ...STROKE_DEFAULT_WIDTH };
  shape: StrokeShape = "circle";
  shapeFilled = false;
  brushPanel = false;
  usedPalette = "starter";
  storage?: FlightStorage;
  profile = emptyProfile();
  designs: FlightDesign[] = [];
  selected = 0;
  main: MainWeapon = "straight";
  private page:
    "hangar" | "editor" | "battle" | "lobby" | "room" | "weapons" | "loadout" =
    "hangar";
  private part: "ship" | "bullet" = "ship";
  private editor?: DrawingEditor;
  private editing?: FlightDesign;
  private view = new CanvasView();
  private board = { x: 24, y: 145, size: 300 };
  private editorBuffer?: HTMLCanvasElement;
  private painted = -1;
  private pointerId: number | null = null;
  private sliding = false;
  private lastPointer?: Pointer;
  run?: RunSave;
  room: FlightRoom | null = null;
  rooms: FlightRoomSummary[] = [];
  private socket?: PlatformSocket;
  private connected = false;
  private reconnectAt = 0;
  private pingAt = 0;
  private seq = 0;
  private lastTick = 0;
  private accumulator = 0;
  private savedAt = 0;
  private sentAt = 0;
  private target?: { x: number; y: number };
  private predicted?: { x: number; y: number };
  private pendingMoveSeq = 0;
  private pilotVisual = new Map<string, { x: number; y: number }>();
  private paused = false;
  private syncing = false;
  private syncAt = Infinity;
  private loading = false;
  private disposed = false;
  private task = false;
  private syncNotice = "";
  private soundOn = true;
  private musicOn = true;
  private feedbackBoss = false;
  private feedbackPhase?: FlightState["phase"];
  private feedbackHp = 0;
  private hapticsOn = true;
  private lastVibration = -Infinity;
  private lastVibrationStrength = 0;
  private feedbackStateId?: string;
  private feedbackKills = 0;
  private lastShot = 0;
  private sizeSliding = false;
  private dragFrom?: { x: number; y: number };
  private dragTo?: { x: number; y: number };
  private textures = new Map<
    string,
    { ship: Sprite; bullet: Sprite; design: FlightDesign }
  >();
  private battleBox = { x: 0, y: 0, scale: 1 };
  private eventId = 0;
  private previews = new Map<string, { stamp: number; ship: Sprite }>();
  private preview?: FlightState;
  constructor(private host: ClientHost) {
    setArtCanvasFactory((w, h) => host.platform.createCanvas(w, h));
    this.soundOn = host.platform.get("flight-sound", true);
    this.musicOn = host.platform.get("flight-music", this.soundOn);
    host.platform.audioSettings?.({ sound: this.soundOn, music: this.musicOn });
    this.hapticsOn = host.platform.get("flight-haptics", true);
  }
  private syncAudio(s = this.state()) {
    if (this.disposed || this.host.screen !== "flight") {
      this.host.platform.silenceAudio?.();
      return;
    }
    let track: import("../../client/flight-audio").FlightMusic = "hangar";
    let soft = this.page === "editor";
    if (this.page === "battle" && s) {
      if ((this.paused && s.phase !== "ended") || this.room?.paused || (this.room && !this.connected)) {
        this.host.platform.silenceAudio?.();
        return;
      }
      track = s.phase === "ended" ? "result" : s.enemies.some((e) => e.boss) ? "boss" : "battle";
      soft = s.phase === "choosing";
    }
    this.host.platform.music?.(this.musicOn ? { track, soft } : null);
  }
  private toggleMusic() {
    this.musicOn = !this.musicOn;
    this.host.platform.set("flight-music", this.musicOn);
    this.host.platform.audioSettings?.({ sound: this.soundOn, music: this.musicOn });
    this.syncAudio();
  }
  private feedback(
    kind: import("../../client/flight-audio").FlightSound,
    strength?: "light" | "medium" | "heavy",
  ) {
    if (this.soundOn) this.host.platform.sound?.(kind);
    if (!strength || !this.hapticsOn) return;
    const now = Date.now();
    const priority = { light: 1, medium: 2, heavy: 3 }[strength];
    // Sustained fire stays gentle; a kill or damage can interrupt a weaker pulse.
    const cooldown = strength === "light" ? 180 : 120;
    if (now - this.lastVibration >= cooldown || priority > this.lastVibrationStrength) {
      this.lastVibration = now;
      this.lastVibrationStrength = priority;
      this.host.platform.vibrate(strength);
    }
  }
  private toast(e: unknown) {
    this.host.toast(e instanceof Error ? e.message : String(e));
  }
  private async work(fn: () => Promise<void>) {
    if (this.task) return;
    this.task = true;
    try {
      await fn();
    } catch (e) {
      this.toast(e);
    } finally {
      this.task = false;
    }
  }
  async open() {
    this.host.platform.set("flight-return", true);
    this.host.go("flight");
    this.page = "hangar";
    this.paused = true;
    this.syncAudio();
    await this.load();
  }
  async load() {
    if (!this.host.player) return;
    if (this.storage?.account === this.host.player.id) return;
    if (this.loading) return;
    this.loading = true;
    try {
      this.storage = new FlightStorage(this.host.platform, this.host.player.id);
      const library = await this.storage.load();
      this.designs = library.designs;
      this.profile = library.profile;
      if (library.activeId)
        this.run = (await this.storage.loadRun(library.activeId)) ?? undefined;
      void this.sync();
      const roomId = this.host.platform.get<string | undefined>(
        "flight-room:" + this.host.player.id,
        undefined,
      );
      if (roomId && this.run?.status !== "active")
        void this.host
          .api<FlightRoom>(ROOT + "/rooms/" + roomId)
          .then((room) => {
            if (
              this.page === "hangar" &&
              !this.room &&
              this.run?.status !== "active"
            )
              this.enterRoom(room);
          })
          .catch((e) => {
            if (e instanceof ApiError && e.status === 404)
              this.host.platform.remove("flight-room:" + this.host.player!.id);
          });
    } catch (e) {
      this.toast(e);
    } finally {
      this.loading = false;
    }
  }
  private options() {
    return {
      color: this.color,
      width: this.eraser ? this.eraserWidth : this.width,
      brush: this.brush,
      tool: this.eraser ? ("eraser" as const) : undefined,
      mirror: this.brush === "mirror",
      shape: this.shape,
      ...this.host.drawingSettings.stroke(this),
    };
  }
  private cancelDrag() {
    this.dragFrom = undefined;
    this.dragTo = undefined;
    this.editor?.clearPreview();
  }
  private trial() {
    const d = this.design(),
      t = this.ensure(d),
      id = this.host.player!.id;
    this.feedbackStateId = undefined;
    this.preview = createFlight("trial", 1, [
      { id, main: this.main, unlocked: this.profile.unlocked },
    ]);
    this.preview.spawnAt = Number.MAX_SAFE_INTEGER;
    this.preview.nextBoss = Number.MAX_SAFE_INTEGER;
    if(this.preview.director){this.preview.director.event='recovery';this.preview.director.eventUntil=Number.MAX_SAFE_INTEGER;}
    this.preview.enemies=Array.from({length:3},(_,i)=>({id:90000+i,kind:0,x:600+i*120,startX:600+i*120,y:600,hp:1e9,maxHp:1e9,age:0,fireAt:Number.MAX_SAFE_INTEGER,phase:0,scale:1,boss:false,radius:25}));
    this.textures.clear();
    this.textures.set(id, t);
    this.page = "battle";
    this.paused = false;
    this.lastTick = Date.now();
    this.target = undefined;
  }
  private async saveLibrary() {
    if (!this.storage) return;
    this.storage.library.designs = this.designs;
    this.storage.library.profile = this.profile;
    await this.storage.saveLibrary();
  }
  private design() {
    return this.designs[this.selected];
  }
  private ensure(d?: FlightDesign) {
    if (!d) throw new Error("先亲手画出战机和子弹吧");
    if (JSON.stringify(d).length > 1600000)
      throw new Error("设计笔迹过多，请精简后保存");
    const ship = makeSprite(this.host.platform, d.ship),
      bullet = makeSprite(this.host.platform, d.bullet);
    if (!ship.w || !ship.h || !bullet.w || !bullet.h)
      throw new Error("战机和子弹都需要有可见笔迹");
    return { ship, bullet, design: copy(d) };
  }
  private newDesign() {
    if (this.designs.length >= 12) {
      this.host.toast("最多保存12套设计，请先删除一套");
      return;
    }
    const d: FlightDesign = {
      id: requestId(),
      revision: 0,
      name: "我的战机 " + (this.designs.length + 1),
      ship: emptyDrawing(),
      bullet: emptyDrawing(),
      shipSize: 92,
      bulletSize: 18,
      updatedAt: Date.now(),
      dirty: true,
    };
    this.designs.push(d);
    this.selected = this.designs.length - 1;
    this.edit("ship");
  }
  private edit(part: "ship" | "bullet") {
    const d = this.design();
    if (!d) return;
    this.editing = copy(d);
    this.part = part;
    this.editor = new DrawingEditor(this.editing[part]);
    this.view.reset();
    this.painted = -1;
    this.page = "editor";
    this.preview = undefined;
  }
  private commitEditor() {
    if (!this.editing || !this.editor) return;
    this.editor.end();
    this.cancelDrag();
    this.editing[this.part] = copy(this.editor.document);
    delete this.editing[this.part].events;
    delete this.editing[this.part].background;
    this.editing.dirty = true;
    this.editing.updatedAt = Math.max(Date.now(), this.editing.updatedAt + 1);
    const i = this.designs.findIndex((d) => d.id === this.editing!.id);
    if (i >= 0) this.designs[i] = copy(this.editing);
    void this.saveLibrary().catch((e) => this.toast(e));
  }
  private async startSolo() {
    this.target = undefined;
    this.predicted = undefined;
    this.pilotVisual.clear();
    await this.load();
    if (this.room) throw new Error("请先离开双人房间");
    if (this.run?.status === "active")
      throw new Error("先继续或结束已保存的挑战");
    const d = this.design(),
      texture = this.ensure(d),
      id = this.host.player!.id,
      seed = Math.floor(Math.random() * 4294967296),
      runId = requestId();
    const manifest = {
      version: 2 as const,
      id: runId,
      seed,
      pilot: { id, main: this.main, unlocked: [...this.profile.unlocked] },
      design: copy(d!),
      dependencies: [] as string[],
    };
    for (const previous of this.storage!.library.runIds) {
      const run = await this.storage!.loadRun(previous);
      if (
        run &&
        run.status === "pending" &&
        (run.state.bosses > 0 || run.state.tick >= 36000)
      )
        manifest.dependencies.push(previous);
    }
    this.run = {
      manifest,
      state: createFlight(runId, seed, [manifest.pilot]),
      chunks: [],
      pending: [],
      acknowledged: 0,
      status: "active",
    };
    await this.storage!.saveRun(this.run);
    this.textures.clear();
    this.textures.set(id, texture);
    this.page = "battle";
    this.paused = false;
    this.lastTick = Date.now();
    this.savedAt = Date.now();
    this.eventId = 0;
  }
  private resumeSolo() {
    if (!this.run) return;
    this.target = undefined;
    this.predicted = undefined;
    this.textures.clear();
    this.textures.set(
      this.host.player!.id,
      this.ensure(this.run.manifest.design),
    );
    this.page = "battle";
    this.paused = false;
    this.lastTick = Date.now();
    this.accumulator = 0;
  }
  private command(action: FlightAction) {
    const s = this.state();
    if (!s) return;
    const id = this.host.player!.id;
    if (this.preview) {
      flightAction(s, id, action);
      return;
    }
    if (this.room) {
      if (action.kind === "move") this.pendingMoveSeq = this.seq + 1;
      if (this.connected)
        void this.socket
          ?.send(
            JSON.stringify({
              type: "input",
              run: this.room.id,
              tick: s.tick,
              seq: ++this.seq,
              action,
            }),
          )
          .catch((e) => this.toast(e));
      return;
    }
    if (this.run && flightAction(s, id, action)) {
      this.run.pending.push({ tick: s.tick, player: id, action });
      if (action.kind === "choose") this.feedback("upgrade", "light");
      if (s.phase === "ended") void this.complete();
    }
  }
  private flushChunk(final = false) {
    if (!this.run) return;
    const r = this.run,
      last = r.chunks[r.chunks.length - 1];
    if (!r.pending.length && last?.until === r.state.tick && !final) return;
    r.chunks.push({
      index: r.chunks.length,
      until: r.state.tick,
      commands: r.pending,
      final,
    });
    r.pending = [];
  }
  private async save() {
    if (!this.run || !this.storage) return;
    this.flushChunk();
    try {
      await this.storage.saveRun(this.run);
    } catch (e) {
      this.paused = true;
      this.toast(e);
    }
  }
  private async complete() {
    const r = this.run;
    if (!r || r.status !== "active") return;
    this.flushChunk(true);
    r.status = "pending";
    this.paused = true;
    if (r.state.bosses && !this.profile.unlocked.includes("homing"))
      this.profile.unlocked.push("homing");
    if (r.state.tick >= 36000 && !this.profile.unlocked.includes("boomerang"))
      this.profile.unlocked.push("boomerang");
    const best = this.profile.solo;
    best.ticks = Math.max(best.ticks, r.state.tick);
    best.bosses = Math.max(best.bosses, r.state.bosses);
    best.level = Math.max(best.level, r.state.level);
    await this.save();
    await this.saveLibrary();
    void this.sync();
  }
  async sync() {
    if (this.disposed || this.syncing || !this.storage || !this.host.player)
      return;
    this.syncing = true;
    const account = this.host.player.id,
      storage = this.storage;
    try {
      try {
        await this.host.api(ROOT + "/profile");
      } catch (e) {
        if (!(e instanceof ApiError) || e.status !== 401) throw e;
        const auth = await this.host.platform.request<{
          token: string;
          player: import("../../shared/account").Player;
        }>("/auth", "POST", await this.host.platform.credentials());
        if (auth.player.id !== account)
          throw new Error("当前微信账号与本机存档不一致，请切回原账号同步");
        this.host.token = auth.token;
        this.host.player = auth.player;
      }
      for (const id of storage.library.runIds) {
        if (this.disposed) return;
        const run =
          this.run?.manifest.id === id ? this.run : await storage.loadRun(id);
        if (
          !run ||
          !["pending", "active"].includes(run.status) ||
          !run.chunks.length
        )
          continue;
        try {
          let result = await this.host.api<{
            next: number;
            status: string;
            stars?: number;
          }>(ROOT + "/runs", "POST", run.manifest);
          run.acknowledged = result.next;
          while (result.next < run.chunks.length) {
            if (this.disposed) return;
            result = await this.host.api(
              ROOT + "/runs/" + id + "/chunks",
              "POST",
              run.chunks[result.next],
            );
            run.acknowledged = result.next;
            await storage.saveRun(run);
          }
          if (result.status === "verified") {
            run.status = "verified";
            run.earned = result.stars;
            run.error = undefined;
            await storage.saveRun(run);
          }
        } catch (e) {
          if (e instanceof ApiError && e.status === 400) {
            run.status = "rejected";
            run.error = e.message;
            await storage.saveRun(run);
          } else throw e;
        }
      }
      for (const d of [...this.designs])
        if (d.dirty) {
          try {
            this.ensure(d);
          } catch {
            continue;
          }
          try {
            const saved = await this.host.api<FlightDesign>(
              ROOT + "/designs",
              "POST",
              d,
            );
            const index = this.designs.findIndex(
              (v) => v.id === d.id && v.updatedAt === d.updatedAt,
            );
            if (index >= 0) this.designs[index] = saved;
          } catch (e) {
            if (e instanceof ApiError && e.status === 409) {
              d.id = requestId();
              d.revision = 0;
              d.name = d.name.slice(0, 20) + " 副本";
              this.host.toast("设计存在冲突，已保留本机副本");
            } else throw e;
          }
        }
      const [remote, profile, player] = await Promise.all([
        this.host.api<{ designs: FlightDesign[] }>(ROOT + "/designs"),
        this.host.api<FlightProfile>(ROOT + "/profile"),
        this.host.api<import("../../shared/account").Player>("/me"),
      ]);
      if (this.disposed || this.host.player?.id !== account) return;
      for (const d of remote.designs) {
        const i = this.designs.findIndex((local) => local.id === d.id);
        if (i < 0 && this.designs.length < 12) this.designs.push(d);
        else if (i >= 0 && !this.designs[i].dirty) this.designs[i] = d;
      }
      for (const id of storage.library.runIds) {
        const r = await storage.loadRun(id);
        if (r?.status === "pending") {
          if (r.state.bosses && !profile.unlocked.includes("homing"))
            profile.unlocked.push("homing");
          if (r.state.tick >= 36000 && !profile.unlocked.includes("boomerang"))
            profile.unlocked.push("boomerang");
        }
      }
      this.profile = profile;
      this.host.player = player;
      this.host.platform.set("flight-account", {
        player,
        token: this.host.token,
      });
      await this.saveLibrary();
      this.syncNotice = "设计与奖励已同步";
      this.host.toast(this.syncNotice);
    } catch (e) {
      this.syncNotice =
        e instanceof Error ? e.message : "暂时离线，记录保留在本机";
      if (this.page === "weapons") this.toast(e);
    } finally {
      this.syncing = false;
      this.syncAt = Date.now() + 30000;
    }
  }
  async lobby() {
    this.host.platform.set("flight-return", true);
    await this.load();
    this.page = "lobby";
    this.host.go("flight");
    try {
      this.rooms = (
        await this.host.api<{ rooms: FlightRoomSummary[] }>(ROOT + "/rooms")
      ).rooms;
    } catch (e) {
      this.toast(e);
    }
  }
  async join(code: string) {
    await this.load();
    if (this.run?.status === "active")
      throw new Error("先保存并结束单人挑战再组队");
    const r = await this.host.api<FlightRoom>(
      ROOT + "/rooms/" + encodeURIComponent(code) + "/join",
      "POST",
      {},
    );
    this.enterRoom(r);
  }
  private enterRoom(r: FlightRoom) {
    this.host.platform.set("flight-room:" + this.host.player!.id, r.id);
    this.room = r;
    this.host.go("flight");
    this.page = r.state ? "battle" : "room";
    this.paused = false;
    this.connect();
  }
  private connect() {
    if (!this.room || !this.host.platform.connectSocket) return;
    this.socket?.close();
    this.connected = false;
    const room = this.room.id;
    this.socket = this.host.platform.connectSocket("/api" + ROOT + "/socket", {
      onOpen: () => {
        void this.socket?.send(
          JSON.stringify({
            type: "auth",
            version: 2,
            token: this.host.token,
            room,
          }),
        );
        this.connected = true;
        this.seq = 0;
        this.pingAt = Date.now();
      },
      onMessage: (data) => {
        try {
          const m = JSON.parse(data);
          if (m.type === "room") {
            const previous = this.room?.state?.id;
            this.room = m.room;
            for (const seat of this.room!.seats)
              if (
                seat.design &&
                this.textures.get(seat.id)?.design.updatedAt !==
                  seat.design.updatedAt
              )
                this.textures.set(seat.id, this.ensure(seat.design));
            if (this.room!.state) {
              if (previous !== this.room!.state.id) this.pilotVisual.clear();
              this.page = "battle";
            }
          } else if (m.type === "state" && this.room) {
            this.room.state = m.state?.packed ? unpackFlight(m.state) : m.state;
            this.room.paused = m.paused;
            this.room.choosingUntil = m.choosingUntil;
            this.room.seats.forEach((s) => {
              const meta = m.seats.find((v: any) => v.id === s.id);
              if (meta) Object.assign(s, meta);
            });
            const me = this.room.state?.pilots.find(
              (p) => p.id === this.host.player?.id,
            );
            if (
              !this.target &&
              this.predicted &&
              me &&
              Math.hypot(me.x - this.predicted.x, me.y - this.predicted.y) < 1
            )
              this.predicted = undefined;
            this.page = "battle";
          } else if (
            m.type === "ack" &&
            m.seq >= this.pendingMoveSeq &&
            !m.accepted
          ) {
            this.predicted = undefined;
          } else if (m.type === "error") {
            this.host.toast(m.message);
            if ([401, 404, 409, 426].includes(m.status)) {
              this.socket?.close();
              this.room = null;
              this.page = "hangar";
              this.host.platform.remove("flight-room:" + this.host.player!.id);
            }
          } else if (m.type === "replaced" || m.type === "closed") {
            this.socket?.close();
            this.room = null;
            this.host.platform.remove("flight-room:" + this.host.player!.id);
            this.page = "hangar";
            this.host.toast(m.message ?? "已在另一处连接房间");
          }
        } catch (e) {
          this.toast(e);
        }
      },
      onClose: () => {
        this.connected = false;
        this.reconnectAt = Date.now() + 1500;
      },
      onError: () => {
        this.connected = false;
        this.reconnectAt = Date.now() + 1500;
      },
    });
  }
  private state() {
    return this.preview ?? this.room?.state ?? this.run?.state;
  }
  update(now: number) {
    this.syncAudio();
    if (
      this.host.screen === "flight" &&
      this.page !== "battle" &&
      this.page !== "editor" &&
      now >= this.syncAt &&
      !this.syncing
    ) {
      this.syncAt = now + 30000;
      void this.sync();
    }
    if (this.room) {
      if (!this.connected && now >= this.reconnectAt) {
        this.reconnectAt = now + 5000;
        this.connect();
      }
      if (this.connected && now - this.pingAt >= 3000) {
        this.pingAt = now;
        void this.socket
          ?.send(JSON.stringify({ type: "ping" }))
          .catch(() => {});
      }
    }
    if (this.host.screen !== "flight") {
      this.lastTick = now;
      return;
    }
    const s = this.state();
    if (this.page !== "battle" || !s) {
      this.lastTick = now;
      return;
    }
    if (this.feedbackStateId !== s.id) {
      this.feedbackStateId = s.id;
      this.feedbackKills = s.kills;
      this.feedbackBoss = s.enemies.some((e) => e.boss);
      this.feedbackPhase = s.phase;
      this.feedbackHp = s.pilots.find((p) => p.id === this.host.player?.id)?.hp ?? 0;
      this.lastShot = s.pilots.find((p) => p.id === this.host.player?.id)?.fireAt ?? 0;
      this.eventId = Math.max(0, ...s.effects.map((e) => e.id));
      this.lastVibration = -Infinity;
      this.lastVibrationStrength = 0;
    }
    if(s.phase!=='playing'||this.room?.paused){this.pointerId=null;this.lastPointer=undefined;this.target=undefined;this.predicted=undefined;}
    if (this.preview) {
      this.accumulator += Math.min(100, Math.max(0, now - this.lastTick));
      while (this.accumulator >= 1000 / 60 && !this.paused) {
        this.preview.pilots[0].invulnerable=120;
        for(const e of this.preview.enemies){e.y=600;e.hp=e.maxHp;}
        if (this.target)
          flightAction(s, this.host.player!.id, {
            kind: "move",
            ...this.target,
          });
        stepFlight(s);
        this.accumulator -= 1000 / 60;
      }
    } else if (this.room) {
      if (
        this.target &&
        now - this.sentAt >= 50 &&
        s.phase === "playing" &&
        !this.room.paused
      ) {
        this.sentAt = now;
        this.command({ kind: "move", ...this.target });
      }
    } else if (this.run?.status === "active" && !this.paused) {
      this.accumulator += Math.min(100, Math.max(0, now - this.lastTick));
      while (this.accumulator >= 1000 / 60 && s.phase === "playing") {
        stepFlight(s);
        this.accumulator -= 1000 / 60;
      }
      if (s.phase !== "playing") this.accumulator = 0;
      if (s.phase === "ended") void this.complete().catch((e) => this.toast(e));
      if (now - this.savedAt >= 5000) {
        this.savedAt = now;
        void this.save();
      }
    }
    const latest = s.effects.filter((e) => e.id > this.eventId);
    // Splash explosions also use "burst", so only the kill counter proves a kill.
    const destroyed = s.kills > this.feedbackKills;
    this.feedbackKills = s.kills;
    const quiet = (this.paused && s.phase !== "ended") || !!this.room?.paused || (!!this.room && !this.connected);
    const myPilot = s.pilots.find((p) => p.id === this.host.player?.id);
    const hasBoss = s.enemies.some((e) => e.boss);
    const enteredBoss = hasBoss && !this.feedbackBoss;
    const finished = s.phase === "ended" && this.feedbackPhase !== "ended";
    const offered = s.phase === "choosing" && this.feedbackPhase !== "choosing";
    const down = !!myPilot && myPilot.hp <= 0 && this.feedbackHp > 0;
    const revived = !!myPilot && myPilot.hp > 0 && this.feedbackHp <= 0;
    this.feedbackBoss = hasBoss;
    this.feedbackPhase = s.phase;
    this.feedbackHp = myPilot?.hp ?? 0;
    this.syncAudio(s);
    if (!quiet && this.soundOn) {
      if (finished) this.host.platform.sound?.("finish");
      else if (down) this.host.platform.sound?.("down");
      else if (revived) this.host.platform.sound?.("revive");
      else if (enteredBoss) this.host.platform.sound?.("warning");
      else if (offered) this.host.platform.sound?.("upgrade");
    }
    if (latest.length || destroyed) {
      this.eventId = Math.max(this.eventId, ...latest.map((e) => e.id));
      const me = this.host.player?.id;
      if (!quiet) {
        if (latest.some((e) => e.kind === "hurt" && e.target === me))
          this.feedback("hurt", "heavy");
        else if (latest.some((e) => e.kind === "boss"))
          this.feedback("boss", "heavy");
        else if (
          latest.some(
            (e) => e.kind === "evolve" && (!e.target || e.target === me),
          )
        )
          this.feedback("evolve", "medium");
        else if (latest.some((e) => e.kind === "ink" && e.target === me))
          this.feedback("skill", "medium");
        else if (destroyed) this.feedback("burst", "medium");
        else if (latest.some((e) => e.kind === "supply"))
          this.feedback("pickup", "light");
        else if (latest.some((e) => e.kind === "burst")) this.feedback("burst", "light");
        else if (latest.some((e) => e.kind === "hit")) this.feedback("hit", "light");
      }
      // A simultaneous reward remains audible without adding another hardware pulse.
      if (!quiet && this.soundOn && latest.some((e) => e.kind === "supply") &&
          (destroyed || latest.some((e) => ["hurt", "boss", "evolve", "ink"].includes(e.kind))))
        this.host.platform.sound?.("pickup");
    }
    if (
      myPilot &&
      myPilot.fireAt !== this.lastShot &&
      s.phase === "playing" &&
      !quiet
    ) {
      this.lastShot = myPilot.fireAt;
      this.feedback(myPilot.main);
    }
    if (quiet && myPilot) this.lastShot = myPilot.fireAt;
    this.lastTick = now;
  }
  pause() {
    this.host.platform.silenceAudio?.();
    this.pointerId = null;
    this.lastPointer = undefined;
    this.target = undefined;
    this.predicted = undefined;
    this.view.clearGesture();
    this.cancelDrag();
    this.editor?.end();
    if (this.page === "editor") this.commitEditor();
    if (this.room) {
      this.socket?.close();
      this.connected = false;
    } else {
      this.paused = true;
      void this.save();
    }
  }
  async dispose() {
    this.disposed = true;
    this.host.platform.silenceAudio?.();
    this.socket?.close();
    this.socket = undefined;
    this.room = null;
    this.paused = true;
    await this.save();
    this.run = undefined;
    this.storage = undefined;
  }
  resize() {
    this.pointerId = null;
    this.lastPointer = undefined;
    this.target = undefined;
    this.predicted = undefined;
    this.view.clearGesture();
  }
  back() {
    if (this.preview) {
      this.preview = undefined;
      this.page = "hangar";
      this.target = undefined;
      return;
    }
    if (this.page === "editor") {
      this.commitEditor();
      this.page = "hangar";
      return;
    }
    if (this.page === "battle") {
      if (this.room)
        void this.work(async () => {
          if (
            await this.host.platform.confirm(
              "离开组队？",
              "离场后不能重新加入本局，将结算已经取得的进度。",
            )
          )
            await this.leave();
        });
      else {
        this.paused = true;
        void this.save();
        this.page = "hangar";
      }
      return;
    }
    if (this.page === "room") {
      void this.work(() => this.leave());
      return;
    }
    if (
      this.page === "lobby" ||
      this.page === "weapons" ||
      this.page === "loadout"
    ) {
      this.page = "hangar";
      return;
    }
    this.host.platform.set("flight-return", false);
    this.host.home();
  }
  private async report() {
    if (!this.room) return;
    const target = this.room.seats.find((s) => s.id !== this.host.player?.id);
    if (!target) return;
    const choice = await this.host.platform.choose("队友与作品", [
      "举报不适当内容",
      "举报隐私问题",
      "屏蔽并离开",
    ]);
    if (choice === null) return;
    if (choice === 2) {
      await this.host.api(
        ROOT + "/rooms/" + this.room.id + "/block",
        "POST",
        {},
      );
      this.room = null;
      this.socket?.close();
      this.page = "hangar";
      return;
    }
    await this.host.api(ROOT + "/rooms/" + this.room.id + "/report", "POST", {
      target: target.id,
      reason: choice === 0 ? "不适当内容" : "隐私问题",
    });
    this.host.toast("举报已提交，作品快照已保存供复核");
  }
  private async leave() {
    if (this.room)
      await this.host.api(
        ROOT + "/rooms/" + this.room.id + "/leave",
        "POST",
        {},
      );
    this.host.platform.remove("flight-room:" + this.host.player!.id);
    this.room = null;
    this.socket?.close();
    this.socket = undefined;
    this.textures.clear();
    this.page = "hangar";
    void this.sync();
  }
  private button(
    id: string,
    label: string,
    x: number,
    y: number,
    w: number,
    action: () => void,
    disabled = false,
    tone: Tone = "cream",
    glyph?: string,
    height = 44,
  ) {
    stickerButton(this.host.paint, "flight-" + id, label, x, y, w, height, action, tone, disabled || this.task, glyph);
  }
  render(now: number) {
    if (this.page === "battle") {
      this.renderBattle(now);
      return;
    }
    const h = this.host.H - this.host.top,
      p = this.host.paint,
      W = this.host.W;
    this.host.header(
      this.page === "editor"
        ? this.part === "ship"
          ? "画出你的战机"
          : "画出你的子弹"
        : this.page === "lobby"
          ? "涂鸦战机 · 组队"
          : this.page === "weapons"
            ? "武器图鉴"
            : "涂鸦战机",
      this.page === "editor"
        ? "透明画纸 · 双指缩放旋转 · 向上飞行"
        : "把想象画出来，一起飞向下一片天空",
    );
    if (this.loading) {
      p.text("正在展开机库…", W / 2, 150, 16, "#52736F", "500", "center");
      return;
    }
    if (this.page === "editor") {
      this.renderEditor(now);
      return;
    }
    const c = this.host.ctx;
    if (this.page === "loadout") {
      const gap = 10,
        cell = (W - 58) / 2,
        rowH = Math.min(122, (h - 176) / 4);
      MAIN_WEAPONS.forEach((w, i) => {
        const x = 24 + (i % 2) * (cell + gap),
          y = 104 + Math.floor(i / 2) * rowH,
          ch = rowH - 10,
          active = this.main === w.id,
          held = p.pressedId === "flight-main-" + w.id,
          yy = y + (held ? 2 : 0);
        box(c, x, y + 4, cell, ch, 16, shade(w.color, -0.45), INK, 2.4);
        box(c, x, yy, cell, ch, 16, active ? linear(c, 0, yy, 0, yy + ch, [[0, "#FFFFFF"], [1, shade(w.color, 0.72)]]) : linear(c, 0, yy, 0, yy + ch, [[0, "#FFFDF6"], [1, "#F2E9D6"]]), INK, active ? 3.2 : 2.4);
        medallion(c, w.id, x + 30, yy + 32, 19, w.color);
        fitText(c, w.name, x + 56, yy + 24, cell - 64, 15, INK, "900");
        fitText(c, w.range, x + 56, yy + 43, cell - 64, 11, shade(w.color, -0.35), "800");
        fitText(c, w.detail, x + 12, yy + ch - 34, cell - 24, 11, "#5B6470", "600");
        fitText(c, "进化 · " + EVOLUTIONS[w.id].name, x + 12, yy + ch - 15, cell - 24, 10.5, "#A0641E", "700");
        if (active) {
          disc(c, x + cell - 14, yy + 14, 11, "#5BE39A", INK, 2);
          icon(c, "barrier", x + cell - 14, yy + 14, 13, "#FFFFFF");
        }
        p.hits.push({
          id: "flight-main-" + w.id,
          label: w.name + " " + w.detail,
          x,
          y,
          w: cell,
          h: ch,
          action: () => {
            this.main = w.id;
            this.page = "hangar";
          },
        });
      });
      fitText(c, weaponInfo(this.main).detail + " · 试飞可体验弹道", 24, h - 40, W - 48, 12, "#6E7584", "700");
      return;
    }
    if (this.page === "weapons") {
      label(c, "副武器最多装备3种 · 局内升级获得", 24, 106, 12, "#6E7584", "700", "left");
      const rowH = Math.min(78, (h - 150) / 6);
      SIDE_WEAPONS.forEach((weapon, i) => {
        const y = 122 + i * rowH,
          owned = this.profile.unlocked.includes(weapon.id),
          color = ["#6FA8FF", "#7FE9FF", "#6BC7A8", "#FFC94D", "#C98BFF", "#FF8A5C"][i % 6];
        box(c, 24, y + 3, W - 48, rowH - 10, 14, "rgba(20,30,50,0.18)", null);
        box(c, 24, y, W - 48, rowH - 10, 14, linear(c, 0, y, 0, y + rowH, [[0, "#FFFDF6"], [1, "#F2E9D6"]]), INK, 2.4);
        medallion(c, weapon.id, 52, y + (rowH - 10) / 2, 18, owned ? color : "#A9AFBB");
        fitText(c, weapon.name, 80, y + (rowH - 10) / 2 - 10, W - 230, 15, INK, "900");
        fitText(c, weapon.detail, 80, y + (rowH - 10) / 2 + 11, W - 230, 11, "#5B6470", "600");
        this.button(
          "buy-" + weapon.id,
          owned ? "已解锁" : weapon.cost ? weapon.cost + " 星" : weapon.achievement!,
          W - 142,
          y + (rowH - 10) / 2 - 20,
          110,
          () =>
            void this.work(async () => {
              const result = await this.host.api<{
                profile: FlightProfile;
                player: import("../../shared/account").Player;
              }>(ROOT + "/unlock/" + weapon.id, "POST", {});
              this.profile = result.profile;
              this.host.player = result.player;
              await this.saveLibrary();
            }),
          owned || !weapon.cost,
          owned ? "teal" : "gold",
          owned ? "barrier" : weapon.cost ? "star" : undefined,
          40,
        );
      });
      return;
    }
    if (this.page === "lobby") {
      const half = (W - 60) / 2;
      this.button("create-public", "创建公开房间", 24, 110, half, () => void this.work(async () => {
        this.enterRoom(await this.host.api(ROOT + "/rooms", "POST", { public: true }));
      }), false, "gold", "team");
      this.button("create-private", "邀请好友", 36 + half, 110, half, () => void this.work(async () => {
        this.enterRoom(await this.host.api(ROOT + "/rooms", "POST", { public: false }));
      }), false, "teal", "plus");
      this.button("code", "输入房间码", 24, 162, half, () => void this.work(async () => {
        const code = await this.host.platform.prompt("加入战机小队", "填写房间码");
        if (code) await this.join(code);
      }), false, "cream", "swap");
      this.button("refresh", "刷新房间", 36 + half, 162, half, () => void this.lobby(), false, "cream", "reroll");
      if (!this.rooms.length) {
        blueprint(c, 24, 224, W - 48, 150, 20);
        for (const [dx, tone] of [[-36, "#7FE9FF"], [36, "#FFB23E"]] as const) {
          icon(c, "wing", W / 2 + dx, 280, 44, tone);
        }
        label(c, "还没有等候中的小队，创建一个吧", W / 2, 340, 13, "#DCE8F5", "700");
      }
      this.rooms.slice(0, Math.max(1, Math.floor((h - 250) / 72))).forEach((r, i) => {
        const y = 224 + i * 72;
        box(c, 24, y + 3, W - 48, 62, 14, "rgba(20,30,50,0.18)", null);
        box(c, 24, y, W - 48, 62, 14, linear(c, 0, y, 0, y + 62, [[0, "#FFFDF6"], [1, "#F2E9D6"]]), INK, 2.4);
        medallion(c, "team", 52, y + 31, 17, "#6FA8FF");
        fitText(c, r.host + "的小队", 80, y + 24, W - 230, 15, INK, "900");
        for (let n = 0; n < 2; n++) disc(c, 86 + n * 16, y + 44, 5, n < r.count ? "#5BE39A" : "rgba(30,40,60,0.15)", INK, 1.2);
        this.button("join-" + r.id, "加入  " + r.count + "/2", W - 138, y + 11, 100, () => void this.work(() => this.join(r.id)), false, "teal", undefined, 40);
      });
      return;
    }
    if (this.page === "room" && this.room) {
      const r = this.room;
      box(c, 24, 102, W - 48, 58, 14, linear(c, 0, 102, 0, 160, [[0, "#FFE08A"], [1, "#F2A93B"]]), INK, 2.6);
      c.save();
      c.setLineDash([5, 5]);
      c.strokeStyle = "rgba(27,33,48,0.45)";
      c.beginPath();
      c.moveTo(W - 150, 108);
      c.lineTo(W - 150, 154);
      c.stroke();
      c.restore();
      label(c, "房间码", 40, 118, 11, "rgba(27,33,48,0.7)", "800", "left");
      label(c, r.code, 40, 140, 22, INK, "900", "left");
      this.button("share", "邀请好友", W - 138, 110, 102, () =>
        void this.host.platform.share(r.code, { room: true, mode: "doodle-flight", title: "一起开着手绘战机出击！" }), false, "cream", "team", 40);
      r.seats.forEach((s, i) => {
        const y = 172 + i * 100;
        blueprint(c, 24, y, W - 48, 88, 16);
        fitText(c, s.name, 42, y + 28, W - 190, 17, "#F4F1E6", "900");
        disc(c, 48, y + 58, 5, s.ready ? "#5BE39A" : "#FFC94D");
        fitText(c, (s.ready ? "已准备" : "正在设计") + " · " + (s.connected ? "已连接" : "连接中"), 60, y + 58.5, W - 210, 12, "#BFD3EA", "700");
        if (s.design) {
          const t = this.textures.get(s.id);
          if (t) {
            pad(c, W - 80, y + 64, 34, now);
            drawSprite(c, t.ship, W - 80, y + 42, 60);
          }
        }
      });
      const by = 172 + Math.max(2, r.seats.length) * 100;
      this.button("loadout", this.design()?.name ?? "先创作战机", 24, by, W - 48, () => {
        this.page = "hangar";
      }, false, "cream", "wing");
      this.button("ready", "使用当前设计准备", 24, by + 52, W - 48, () => void this.work(async () => {
        this.ensure(this.design());
        this.room = await this.host.api(ROOT + "/rooms/" + r.id + "/ready", "POST", { design: this.design(), main: this.main, ready: true });
      }), false, "teal", "barrier");
      this.button("start-coop", r.host === this.host.player?.id ? "小队出击" : "等待房主出击", 24, by + 104, W - 48, () => void this.work(async () => {
        this.room = await this.host.api(ROOT + "/rooms/" + r.id + "/start", "POST", {});
      }), r.host !== this.host.player?.id, "gold", "play", 50);
      return;
    }
    const d = this.design(),
      heroH = Math.max(150, Math.min(270, h - 468)),
      heroY = 104;
    blueprint(c, 24, heroY, W - 48, heroH, 24);
    box(c, 36, heroY + 12, Math.min(W - 72, 220), 34, 17, "rgba(10,14,30,0.55)", "rgba(255,255,255,0.25)", 1.5);
    fitText(c, d?.name ?? "第一架战机，由你来画", 50, heroY + 29.5, Math.min(W - 100, 192), 15, "#F4F1E6", "900");
    const shipY = heroY + heroH * 0.56;
    pad(c, W / 2, shipY + Math.min(135, heroH - 60) * 0.42, Math.min(70, heroH * 0.3), now);
    if (d) {
      try {
        let cached = this.previews.get(d.id);
        if (!cached || cached.stamp !== d.updatedAt) {
          cached = { stamp: d.updatedAt, ship: makeSprite(this.host.platform, d.ship) };
          this.previews.set(d.id, cached);
        }
        drawSprite(c, cached.ship, W / 2, shipY + Math.sin(now / 500) * 3, Math.min(140, heroH - 66));
      } catch {
        /* Draft can be incomplete. */
      }
    } else {
      icon(c, "wing", W / 2, shipY, 70, "#7FE9FF");
      label(c, "飞机可以是猫，也可以是一朵云", W / 2, heroY + heroH - 22, 12, "#BFD3EA", "700");
    }
    const weapon = weaponInfo(this.main);
    if (d) {
      disc(c, 50, heroY + heroH - 30, 16, weapon.color, INK, 2);
      icon(c, this.main, 50, heroY + heroH - 30, 18, "#FFFFFF");
      fitText(c, weapon.name, 72, heroY + heroH - 29.5, W / 2 - 60, 12, "#F4F1E6", "800");
      this.button("trial", "试飞", W - 112, heroY + heroH - 52, 76, () => {
        try {
          this.trial();
        } catch (e) {
          this.toast(e);
        }
      }, false, "sky", "play", 40);
    }
    if (this.designs.length > 1) {
      roundButton(p, "flight-previous", "‹", "left", 46, heroY + heroH / 2, 18, () => {
        this.selected = (this.selected + this.designs.length - 1) % this.designs.length;
      }, "dark", this.task);
      roundButton(p, "flight-next", "›", "right", W - 46, heroY + heroH / 2, 18, () => {
        this.selected = (this.selected + 1) % this.designs.length;
      }, "dark", this.task);
      label(c, `${this.selected + 1} / ${this.designs.length}`, W / 2, heroY + 29.5, 11, "#BFD3EA", "800");
    }
    let y = heroY + heroH + 12;
    const cell = (W - 68) / 3;
    this.button("new", "新设计", 24, y, cell, () => this.newDesign(), false, "cream", "plus");
    this.button("edit-ship", "画战机", 34 + cell, y, cell, () => (d ? this.edit("ship") : this.newDesign()), false, "cream", "wing");
    this.button("edit-bullet", "画子弹", 44 + cell * 2, y, cell, () => (d ? this.edit("bullet") : this.newDesign()), false, "cream", "fan");
    y += 54;
    const held = p.pressedId === "flight-main-select",
      yy = y + (held ? 2 : 0);
    box(c, 24, y + 4, W - 48, 54, 16, shade(weapon.color, -0.45), INK, 2.4);
    box(c, 24, yy, W - 48, 54, 16, linear(c, 0, yy, 0, yy + 54, [[0, "#FFFDF6"], [1, shade(weapon.color, 0.7)]]), INK, 2.4);
    medallion(c, this.main, 50, yy + 26, 17, weapon.color);
    fitText(c, weapon.name, 76, yy + 18, W - 200, 15, INK, "900");
    fitText(c, weapon.range + " · " + weapon.detail, 76, yy + 37, W - 200, 11, "#5B6470", "600");
    label(c, "更换主武器 ›", W - 40, yy + 27, 12, shade(weapon.color, -0.45), "800", "right");
    p.hits.push({ id: "flight-main-select", label: weapon.name + " · 更换主武器", x: 24, y, w: W - 48, h: 54, action: () => (this.page = "loadout") });
    y += 66;
    const half = (W - 60) / 2;
    this.button("solo", this.run?.status === "active" ? "继续单人挑战" : "单人出击", 24, y, half, () => void this.work(async () => {
      if (this.run?.status === "active") this.resumeSolo();
      else await this.startSolo();
    }), false, "gold", "play", 56);
    this.button("coop", this.room ? "返回小队" : "双人组队", 36 + half, y, half, () => {
      if (this.room) this.page = "room";
      else void this.lobby();
    }, false, "teal", "team", 56);
    y += 66;
    this.button("weapons", "武器图鉴", 24, y, half, () => {
      this.page = "weapons";
    }, false, "cream", "orbit");
    this.button(
      "sync",
      this.syncing ? "同步中…" : this.syncNotice && !this.syncNotice.includes("已同步") ? "重试同步" : "同步设计与奖励",
      36 + half,
      y,
      half,
      () => void this.sync(),
      this.syncing,
      "cream",
      "reroll",
    );
    y += 54;
    let sx = 24;
    for (const [glyph, value, tone] of [
      ["clock", `最佳 ${Math.floor(this.profile.solo.ticks / 3600)} 分钟`, "#6BC7A8"],
      ["boss", `Boss ${this.profile.solo.bosses}`, "#FF8A8A"],
      ["star", `今日 ${this.profile.todayStars}/100 星`, "#FFC94D"],
    ] as const) {
      c.font = `800 11px ${FONT}`;
      const w = c.measureText(value).width + 34;
      if (sx + w > W - 24) break;
      box(c, sx, y, w, 24, 12, "rgba(35,53,90,0.08)", "rgba(35,53,90,0.25)", 1.2);
      icon(c, glyph, sx + 13, y + 12, 13, tone);
      label(c, value, sx + 25, y + 12.5, 11, "#46506A", "800", "left");
      sx += w + 6;
    }
    roundButton(p, "flight-music", this.musicOn ? "音乐开" : "音乐关", this.musicOn ? "music" : "mute", W - 42, Math.min(h - 29, y + 53), 19, () => this.toggleMusic(), "cream", this.task, !this.musicOn);
    if (d) {
      p.hits.push({
        id: "flight-design-name",
        label: "重命名设计",
        x: 36,
        y: heroY + 8,
        w: Math.min(W - 72, 220),
        h: 42,
        action: () =>
          void this.work(async () => {
            const name = await this.host.platform.prompt("设计名称", "最多24字", d.name);
            if (name?.trim()) {
              d.name = name.trim().slice(0, 24);
              d.dirty = true;
              d.updatedAt = Date.now();
              await this.saveLibrary();
            }
          }),
      });
      this.button("delete", "删除设计", 24, Math.min(h - 48, y + 34), 110, () => void this.work(async () => {
        if (!(await this.host.platform.confirm("删除这套设计？", "已出击的战斗仍使用原设计。"))) return;
        if (d.revision > 0) await this.host.api(ROOT + "/designs/" + d.id, "DELETE");
        this.designs = this.designs.filter((v) => v.id !== d.id);
        this.selected = 0;
        await this.saveLibrary();
      }), false, "cream", "exit", 38);
    }
  }
  private renderEditor(now: number) {
    const p = this.host.paint,
      W = this.host.W,
      h = this.host.H - this.host.top,
      ed = this.editor!,
      d = this.editing!;
    const toolsY = h - 174,
      size = Math.min(W - 48, toolsY - 205);
    this.board = { x: (W - size) / 2, y: 158, size };
    const b = this.board;
    this.button(
      "switch-part",
      this.part === "ship" ? "接着画子弹" : "返回画战机",
      24,
      104,
      140,
      () => {
        this.commitEditor();
        this.edit(this.part === "ship" ? "bullet" : "ship");
      },
    );
    this.button("save-design", "保存设计", W - 128, 104, 104, () => {
      this.commitEditor();
      this.page = "hangar";
    });
    if (!this.editorBuffer)
      this.editorBuffer = this.host.platform.createCanvas(512, 512);
    if (this.painted !== ed.revision) {
      renderTransparentDrawing(
        this.editorBuffer.getContext("2d")!,
        {
          ...ed.document,
          strokes: [...ed.document.strokes, ...ed.previewStrokes],
        },
        512,
      );
      this.painted = ed.revision;
    }
    const c = this.host.ctx;
    c.save();
    c.fillStyle = "#E0E7E5";
    c.fillRect(b.x, b.y, b.size, b.size);
    this.view.apply(c, b);
    for (let y = 0; y < 1000; y += 50)
      for (let x = 0; x < 1000; x += 50) {
        c.fillStyle = (x / 50 + y / 50) % 2 ? "#EFF2EE" : "#FFFFFF";
        c.fillRect(x, y, 50, 50);
      }
    c.drawImage(this.editorBuffer, 0, 0, 1000, 1000);
    c.strokeStyle = "#83AFA966";
    c.lineWidth = 3;
    c.setLineDash([12, 10]);
    c.strokeRect(80, 80, 840, 840);
    c.setLineDash([]);
    c.restore();
    // Inked frame plus a nose-up hint: sprites always fly towards the top.
    box(c, b.x - 2, b.y - 2, b.size + 4, b.size + 4, 6, "rgba(0,0,0,0)", INK, 3);
    box(c, b.x + b.size - 86, b.y + 8, 78, 24, 12, "rgba(22,28,46,0.72)", null);
    icon(c, "rate", b.x + b.size - 72, b.y + 20, 13, "#7FE9FF", "#0E121C");
    label(c, this.part === "ship" ? "机头朝上" : "向上飞", b.x + b.size - 41, b.y + 20.5, 11, "#F4F1E6", "800");
    const sizeKey = this.part === "ship" ? "shipSize" : "bulletSize",
      min = this.part === "ship" ? 72 : 12,
      max = this.part === "ship" ? 112 : 24;
    const y = b.y + b.size + 5;
    p.text(
      `${this.part === "ship" ? "战机" : "子弹"}尺寸 ${d[sizeKey]} · ${min}～${max}`,
      24,
      y + 14,
      12,
      "#5C7474",
    );
    this.button("smaller", "−", W - 116, y, 40, () => {
      d[sizeKey] = Math.max(min, d[sizeKey] - 2);
    });
    this.button("larger", "＋", W - 70, y, 40, () => {
      d[sizeKey] = Math.min(max, d[sizeKey] + 2);
    });
    p.line(
      [
        [24, y + 34],
        [W - 132, y + 34],
      ],
      "#B4CCC5",
      3,
    );
    p.circle(
      24 + ((d[sizeKey] - min) / (max - min)) * (W - 156),
      y + 34,
      6,
      "#448F86",
    );
    p.hits.push({
      id: "flight-size-slider",
      label: "素材尺寸",
      x: 20,
      y: y + 21,
      w: W - 148,
      h: 22,
      drag: true,
      action: () => {},
    });
    this.usedPalette = this.host.paletteSelection.id;
    renderDrawingTools(
      this.host,
      this,
      toolsY,
      168,
      size,
      {
        palette: () => {
          this.commitEditor();
          this.host.shopReturn = "flight";
          this.host.go("shop");
        },
        undo: () => ed.undo(),
        redo: () => ed.redo(),
        clear: () => ed.clear(),
        fill: () => {
          this.host.toast("透明素材用实心形状上色，背景保持透明");
        },
        cancelLine: () => this.cancelDrag(),
        canUndo: ed.canUndo,
        canRedo: ed.canRedo,
      },
      "flight-",
    );
    if (this.brushPanel)
      renderBrushCatalog(
        this.host,
        this,
        { y: toolsY - 236, height: 236 },
        size,
        (brush) => {
          this.host.drawingSettings.select(brush);
        },
        "flight-",
      );
    if (this.view.changed)
      this.button("reset-view", "复位", b.x + 8, b.y + 8, 62, () =>
        this.view.reset(),
      );
    if (now < 0) return;
  }
  private renderBattle(now: number) {
    const s = this.state();
    if (!s) {
      this.page = "hangar";
      return;
    }
    const p = this.host.paint,
      c = this.host.ctx,
      W = this.host.W,
      h = this.host.H - this.host.top;
    const visible = this.room
      ? {
          ...s,
          pilots: s.pilots.map((pilot) => {
            let v = this.pilotVisual.get(pilot.id);
            if (!v) {
              v = { x: pilot.x, y: pilot.y };
              this.pilotVisual.set(pilot.id, v);
            }
            if (pilot.id === this.host.player?.id && s.version === 2) {
              const target = this.predicted ?? pilot;
              v.x = target.x;
              v.y = target.y;
            } else {
              v.x += (pilot.x - v.x) * 0.4;
              v.y += (pilot.y - v.y) * 0.4;
            }
            return { ...pilot, x: v.x, y: v.y };
          }),
        }
      : s;
    this.battleBox = paintFlight(c, visible, this.textures, W, h, 0, this.host.player?.id, this.host.top);
    const me = s.pilots.find((v) => v.id === this.host.player?.id),
      boss = s.enemies.find((e) => e.boss),
      dark = s.scene % 6 === 2;
    // Damage feedback: red vignette on hits and a slow pulse at low health.
    const hurt = s.effects.find((e) => e.kind === "hurt" && e.target === me?.id && e.age < 18),
      low = !!me && me.hp > 0 && me.hp / me.maxHp < 0.3,
      pain = Math.max(hurt ? 1 - hurt.age / 18 : 0, low ? 0.35 + 0.25 * Math.sin(now / 180) : 0);
    if (pain > 0) {
      c.fillStyle = radial(c, W / 2, h / 2, Math.min(W, h) * 0.35, Math.max(W, h) * 0.75, [
        [0, "rgba(255,40,70,0)"],
        [1, `rgba(255,40,70,${0.55 * pain})`],
      ]);
      c.fillRect(0, -this.host.top, W, h + this.host.top);
    }
    // Keep the battlefield visible through the HUD; only the text stays opaque.
    const pw = Math.min(W - 80, 236);
    glass(c, 10, 8, pw, 46, 10, 0.24, false);
    const clock = `${Math.floor(s.tick / 3600)}:${String(Math.floor(s.tick / 60) % 60).padStart(2, "0")}`;
    numberText(c, clock, 18, 21, 16, "#FFFFFF");
    c.font = `900 16px ${NUM_FONT}`;
    const lx = 28 + c.measureText(clock).width;
    outlined(c, "Lv." + s.level, lx, 21, 12, "#FFE08A", "left");
    if (this.preview) outlined(c, "试飞", lx + 54, 21, 11, "#9FF4FF", "left");
    icon(c, "boss", 10 + pw - 38, 21, 14, "#FF8A8A");
    numberText(c, String(s.bosses), 10 + pw - 12, 21, 12, "#FFFFFF", "right");
    const hpK = me ? me.hp / Math.max(1, me.maxHp) : 0;
    icon(c, "health", 23, 37, 13, "#FF5C7A");
    meter(c, 34, 32, pw - 72, 10, hpK, hpK > 0.5 ? "#5BE39A" : hpK > 0.3 ? "#FFC94D" : "#FF4D6A", me ? me.shield / Math.max(1, me.maxHp) : 0);
    numberText(c, String(Math.max(0, Math.ceil(me?.hp ?? 0))), 10 + pw - 10, 37, 12, "#FFFFFF", "right");
    const xpK = s.xp / (s.version === 1 ? 30 + s.level * 16 : xpRequired(s.level));
    meter(c, 18, 47, pw - 16, 5, xpK, "#7FE9FF");
    const evo = me && s.version === 2 ? EVOLUTIONS[me.main] : undefined,
      evolved = !!me?.levels[`evolve-${me.main}`],
      evolutionNeeds = evo && me ? evo.requires.map((id) => `${upgradeInfo(id).name} ${Math.min(2, me.levels[id] ?? 0)}/2`).join(" · ") : "";
    roundButton(
      p,
      "flight-battle-back",
      this.preview ? "退出" : this.room ? "离场" : this.paused ? "继续" : "暂停",
      this.preview || this.room ? "exit" : this.paused ? "play" : "pause",
      W - 32,
      34,
      22,
      () => {
        if (this.room || this.preview) this.back();
        else {
          this.paused = !this.paused;
          this.lastTick = Date.now();
          if (this.paused) void this.save();
        }
      },
      "dark",
      this.task,
    );
    const y = 60;
    // During boss fights waves pause, so the boss bar takes the chip row.
    if (boss) {
      const bw = Math.min(W - 24, 420),
        bx = (W - bw) / 2,
        k = Math.max(0, boss.hp / boss.maxHp),
        phase = Math.floor(boss.age / 300) % 3;
      glass(c, bx, y, bw, 32, 10, 0.3, false);
      icon(c, "boss", bx + 16, y + 16, 17, "#FF8A8A");
      c.save();
      c.shadowColor = "#101624";
      c.shadowBlur = 3;
      c.shadowOffsetY = 1;
      fitText(c, BOSSES[boss.kind % BOSSES.length], bx + 32, y + 10, bw - 102, 12, "#FFE3E3", "900");
      c.restore();
      for (let i = 0; i < 3; i++) disc(c, bx + bw - 40 + i * 12, y + 10, 3.5, i === phase ? "#FF3D8B" : "rgba(255,255,255,0.25)", INK, 1.2);
      meter(c, bx + 32, y + 21, bw - 44, 8, k, "#FF4D6A", 0, 10);
    } else if (s.version === 1) {
      chip(c, 10, y, "旧版存档 · 结束后新开局体验新版", "clock", "#B79A6B", W - 20, "#F4F1E6", true);
    } else if (me) {
      const d = s.director;
      if (d) {
        const seconds = Math.max(0, Math.ceil((d.eventUntil - s.tick) / 60)),
          progress =
            d.event === "formation"
              ? ` ${Math.min(d.eventTarget, s.kills - d.killsAt)}/${d.eventTarget}`
              : d.eventDone
                ? " ✓"
                : "";
        chip(c, 10, y, `${EVENT_NAMES[d.event]}${progress} · ${seconds}秒`, EVENT_GLYPH[d.event], EVENT_COLOR[d.event], W - 20, "#F4F1E6", true);
      }
    }
    // Secondary equipment information lives at the bottom, away from incoming enemies.
    if (me && evo) {
      const progress = evo.requires.reduce((sum, id) => sum + Math.min(2, me.levels[id] ?? 0), 0),
        overdrive = (me.overdriveUntil ?? 0) > s.tick,
        name = evolved ? evo.name : weaponInfo(me.main).name,
        detail = overdrive ? "超载 ×2" : evolved ? "已进化" : `进化 ${progress}/4`;
      chip(c, 10, h - 80, `${name} · ${detail}`, me.main, evolved ? "#FFC94D" : weaponInfo(me.main).color, W - 120, overdrive ? "#FFE08A" : "#F4F1E6", true);
    }
    // Bottom controls: ink burst (skill), feedback toggles, teammate report.
    const blocked = s.phase !== "playing" || this.paused || !!this.room?.paused,
      ready = !!me && !me.cooldown && me.hp > 0,
      sx = W - 56,
      sy = h - 66;
    if (ready && !blocked) glow(c, sx, sy, 58 + Math.sin(now / 220) * 6, "#FFC94D", 0.55);
    roundButton(p, "flight-skill", me?.cooldown ? `${Math.ceil(me.cooldown / 60)}秒` : "墨爆", "ink", sx, sy, 36, () => this.command({ kind: "skill" }), ready ? "gold" : "dark", !ready || blocked || this.task);
    if (me?.cooldown) {
      sweep(c, sx, sy, 36, me.cooldown / (Math.max(20, 45 - (me.levels.cooldown ?? 0) * 5) * 60));
      numberText(c, String(Math.ceil(me.cooldown / 60)), sx, sy + 1, 20, "#FFFFFF", "center");
    }
    outlined(c, "墨爆", sx, sy + 46, 12, ready ? "#FFE08A" : "#C9CFDA");
    roundButton(p, "flight-sound", this.soundOn ? "音效开" : "音效关", this.soundOn ? "sound" : "mute", 30, h - 32, 19, () => {
      this.soundOn = !this.soundOn;
      this.host.platform.set("flight-sound", this.soundOn);
      this.host.platform.audioSettings?.({ sound: this.soundOn, music: this.musicOn });
    }, "dark", this.task, !this.soundOn);
    roundButton(p, "flight-haptics", this.hapticsOn ? "震动开" : "震动关", this.hapticsOn ? "vibrate" : "still", 74, h - 32, 19, () => {
      this.hapticsOn = !this.hapticsOn;
      this.host.platform.set("flight-haptics", this.hapticsOn);
      if (this.hapticsOn) this.feedback("pickup", "light");
    }, "dark", this.task, !this.hapticsOn);
    roundButton(p, "flight-music", this.musicOn ? "音乐开" : "音乐关", this.musicOn ? "music" : "mute", 118, h - 32, 19, () => this.toggleMusic(), "dark", this.task, !this.musicOn);
    if (this.room) roundButton(p, "flight-report", "队友", "team", 162, h - 32, 19, () => void this.work(() => this.report()), "dark", this.task);
    const hintX = this.room ? 190 : 146, hintWidth = sx - 42 - hintX;
    if (hintWidth >= 40) fitText(c, this.preview ? "试飞不产生奖励" : SCENES[s.scene % SCENES.length], hintX, h - 31, hintWidth, 11, dark ? "#C9D6F0" : "#FFFFFF", "800");
    if (s.version === 2 && me?.offeredWeapon) {
      const bw = Math.min(W - 150, 260);
      stickerButton(p, "flight-swap", "换装 " + weaponInfo(me.offeredWeapon).name, Math.max(10, (W - bw) / 2 - 30), h - 150, bw, 50, () => this.command({ kind: "swap" }), "gold", s.phase !== "playing" || this.paused || this.task, me.offeredWeapon, "保留等级 · 专精仅对应主炮");
    }
    if (this.room?.paused || (this.room && !this.connected)) {
      dim(c, W, h, this.host.top);
      const cw = Math.min(W - 48, 360),
        x0 = (W - cw) / 2,
        y0 = h / 2 - 80;
      paper(c, x0, y0, cw, 150);
      for (let i = 0; i < 8; i++) disc(c, W / 2 + Math.cos(now / 160 + i * 0.785) * 18, y0 + 44 + Math.sin(now / 160 + i * 0.785) * 18, 2 + i * 0.5, rgba("#23A99A", 0.3 + i * 0.09));
      label(c, "等待队友重新连接", W / 2, y0 + 92, 19, INK, "900");
      label(c, "战斗已暂停 · 重连额度每人累计30秒", W / 2, y0 + 120, 12, "#6E7584", "600");
      return;
    }
    if (s.phase === "ended") {
      dim(c, W, h, this.host.top);
      const cw = Math.min(W - 32, 400),
        x0 = (W - cw) / 2,
        ch = 318,
        y0 = Math.max(110, h / 2 - ch / 2);
      paper(c, x0, y0, cw, ch);
      ribbon(c, "航行结束", W / 2, y0, Math.min(200, cw - 80), "#23A99A");
      label(c, "这次涂鸦，飞得很精彩", W / 2, y0 + 50, 19, INK, "900");
      statTiles(c, x0 + 18, y0 + 72, cw - 36, [
        ["clock", `${Math.floor(s.tick / 60)}秒`, "生存"],
        ["star", `Lv.${s.level}`, "等级"],
        ["boss", String(s.bosses), "Boss"],
        ["blast", String(s.kills), "击破"],
      ]);
      const status = this.room
        ? "进度已由服务器结算"
        : this.run?.status === "verified"
          ? `已到账 ${this.run.earned ?? 0} 星`
          : this.run?.status === "rejected"
            ? "记录核验失败，已保留本机记录"
            : `待同步星星 ${starsFor(s.tick, s.bosses)}（受每日上限影响）`;
      icon(c, "star", x0 + 36, y0 + 178, 20, "#FFC94D");
      fitText(c, status, x0 + 52, y0 + 179, cw - 80, 14, "#A0641E", "800");
      fitText(c, this.run?.status === "rejected" ? (this.run.error ?? "") : (s.reason ?? ""), W / 2, y0 + 208, cw - 48, 12, "#6E7584", "600", "center");
      stickerButton(p, "flight-result-back", "返回机库", x0 + 24, y0 + ch - 76, cw - 48, 50, () => {
        if (this.room) void this.work(() => this.leave());
        else {
          this.page = "hangar";
          void this.sync();
        }
      }, "gold", this.task, "exit");
      return;
    }
    if (s.phase === "choosing" && me) {
      dim(c, W, h, this.host.top);
      const cw = Math.min(W - 20, 440),
        x0 = (W - cw) / 2,
        cardH = 92,
        y0 = Math.max(70, h / 2 - 205);
      ribbon(c, "整备强化 · 三选一", W / 2, y0, Math.min(cw - 70, 250), "#23A99A");
      if (evo) {
        fitText(c, `${weaponInfo(me.main).name} → ${evo.name}${evolved ? " · 已进化" : ""}`, W / 2, y0 + 26, cw - 20, 12, "#FFE08A", "700", "center");
        fitText(c, evolutionNeeds, W / 2, y0 + 42, cw - 20, 11, "#E6EEF5", "700", "center");
      } else label(c, "选择后立即生效", W / 2, y0 + 32, 12, "#E6EEF5", "700");
      if (!me.choices.length) {
        paper(c, x0 + 20, y0 + 60, cw - 40, 90);
        label(c, "已选好，等待队友…", W / 2, y0 + 105, 17, INK, "900");
      }
      me.choices.forEach((id, i) => {
        const item = upgradeInfo(id),
          evolution = item.group === "进化",
          cy = y0 + 50 + i * (cardH + 10),
          held = p.pressedId === "flight-upgrade-" + id,
          color = evolution ? "#FFC94D" : item.main ? weaponInfo(item.main).color : (GROUP_COLORS[item.group] ?? "#6BC7A8"),
          level = me.levels[id] ?? 0,
          yy = cy + (held ? 2 : 0);
        box(c, x0 + 2, cy + 5, cw - 4, cardH, 18, "rgba(8,10,20,0.4)", null);
        box(c, x0, yy, cw, cardH, 18, evolution ? linear(c, x0, 0, x0 + cw, 0, [[0, "#FFF3C4"], [1, "#FFD37A"]]) : linear(c, 0, yy, 0, yy + cardH, [[0, "#FFFDF6"], [1, "#F2E9D6"]]), INK, 3);
        box(c, x0 + 3, yy + 3, 10, cardH - 6, 6, color, null);
        medallion(c, upgradeGlyph(id), x0 + 50, yy + cardH / 2, 26, color, evolution);
        fitText(c, item.name, x0 + 88, yy + 26, cw - 190, 18, evolution ? "#8A4B00" : INK, "900");
        c.font = `800 11px ${FONT}`;
        const tag = evolution ? "★ 进化" : item.group,
          tw = c.measureText(tag).width + 16;
        box(c, x0 + cw - tw - 14, yy + 15, tw, 22, 11, rgba(color, 0.22), color, 1.5);
        label(c, tag, x0 + cw - tw / 2 - 14, yy + 26.5, 11, shade(color, -0.45), "800");
        fitText(c, s.version === 2 ? upgradeDescription(me, id) : item.detail, x0 + 88, yy + 51, cw - 104, 12, "#5B6470", "600");
        if (item.cap > 0 && !evolution)
          for (let n = 0; n < item.cap; n++) box(c, x0 + 88 + n * 16, yy + 68, 12, 8, 3, n < level ? color : n === level ? rgba(color, 0.45) : "rgba(30,40,60,0.12)", INK, 1.2);
        else label(c, evolution ? "解锁主炮终极形态" : `第 ${level + 1} 级 · 可持续叠加`, x0 + 88, yy + 72, 11, evolution ? "#8A4B00" : "#7C8594", "700", "left");
        p.hits.push({ id: "flight-upgrade-" + id, label: item.name, x: x0, y: cy, w: cw, h: cardH, action: () => this.command({ kind: "choose", upgrade: id }) });
      });
      const by = y0 + 50 + 3 * (cardH + 10) + 4;
      if (s.version === 2 && me.choices.length)
        stickerButton(p, "flight-reroll", `换一批 (${me.rerolls ?? 0})`, x0 + cw - 150, by, 150, 44, () => this.command({ kind: "reroll" }), "sky", !me.rerolls || this.task, "reroll");
      if (this.room) label(c, `${Math.max(0, Math.ceil((this.room.choosingUntil - now) / 1000))}秒后自动选择`, x0 + 6, by + 20, 12, "#E6EEF5", "700", "left");
      return;
    }
    if (this.paused) {
      dim(c, W, h, this.host.top);
      const cw = Math.min(W - 40, 380),
        x0 = (W - cw) / 2,
        ch = evo ? 350 : 300,
        y0 = h / 2 - ch / 2;
      paper(c, x0, y0, cw, ch);
      ribbon(c, "画纸暂歇", W / 2, y0, 160, "#3E9BDC");
      label(c, "灵感还在，随时继续", W / 2, y0 + 46, 15, "#5B6470", "700");
      statTiles(c, x0 + 18, y0 + 66, cw - 36, [
        ["clock", clock, "时间"],
        ["star", `Lv.${s.level}`, "等级"],
        ["blast", String(s.kills), "击破"],
        ["boss", String(s.bosses), "Boss"],
      ]);
      if (evo && me) {
        fitText(c, `${weaponInfo(me.main).name} → ${evo.name}${evolved ? " · 已进化" : ""}`, W / 2, y0 + 156, cw - 36, 13, "#956116", "800", "center");
        fitText(c, evolutionNeeds, W / 2, y0 + 179, cw - 36, 12, "#5B6470", "700", "center");
      }
      const actionY = y0 + (evo ? 210 : 160);
      stickerButton(p, "flight-resume", "继续飞行", x0 + 22, actionY, cw - 44, 52, () => {
        this.paused = false;
        this.lastTick = Date.now();
      }, "gold", this.task, "play");
      const half = (cw - 56) / 2;
      stickerButton(p, "flight-save-return", "保存并回机库", x0 + 22, actionY + 64, half, 48, () => {
        void this.save();
        this.page = "hangar";
      }, "cream", this.task, "exit");
      stickerButton(p, "flight-end", "结束结算", x0 + 34 + half, actionY + 64, half, 48, () => this.command({ kind: "end" }), "danger", this.task, "star");
    }
  }
  pointer(kind: "down" | "move" | "up" | "cancel", point: Pointer) {
    if (this.page === "editor" && this.editor) {
      const ed = this.editor,
        b = this.board;
      const gesture = this.view.pointer(kind, point, b);
      if (gesture.started) {
        ed.cancelStroke();
        this.cancelDrag();
        this.pointerId = null;
        this.painted = -1;
      }
      if (gesture.consumed) return true;
      if (kind === "down") {
        const hit = [...this.host.paint.hits]
          .reverse()
          .find(
            (h) =>
              !h.disabled &&
              point.x >= h.x &&
              point.x <= h.x + h.w &&
              point.y >= h.y &&
              point.y <= h.y + h.h,
          );
        if (hit) {
          if (hit.id === "flight-size-slider") {
            this.sizeSliding = true;
            this.pointerId = point.id;
          } else if (hit.id === "flight-width-slider") {
            this.sliding = true;
            this.pointerId = point.id;
          } else hit.action();
          return true;
        }
        if (!this.view.containsPaper(b, point) || this.brushPanel) return false;
        const p = this.view.toDocument(b, point, true);
        this.pointerId = point.id;
        if (!this.eraser && DRAG_BRUSHES.includes(this.brush)) {
          this.dragFrom = p;
          this.dragTo = p;
          ed.previewLine(p, p, this.options());
        } else ed.start(p.x, p.y, this.options(), Date.now());
        this.painted = -1;
        return true;
      }
      if (point.id !== this.pointerId) return false;
      if (this.sizeSliding) {
        if (kind === "move" && this.editing) {
          const key = this.part === "ship" ? "shipSize" : "bulletSize",
            min = this.part === "ship" ? 72 : 12,
            max = this.part === "ship" ? 112 : 24;
          this.editing[key] = Math.round(
            Math.max(
              min,
              Math.min(
                max,
                min + ((point.x - 24) / (this.host.W - 156)) * (max - min),
              ),
            ),
          );
        }
      } else if (this.sliding) {
        if (kind === "move")
          this.host.drawingSettings.width(
            this,
            Math.max(
              1,
              Math.min(
                70,
                Math.round(((point.x - 94) / (this.host.W - 121)) * 69 + 1),
              ),
            ),
          );
      } else if (kind === "move") {
        const p = this.view.toDocument(b, point, true);
        if (this.dragFrom) {
          this.dragTo = p;
          ed.previewLine(this.dragFrom, p, this.options());
        } else ed.move(p.x, p.y, Date.now());
        this.painted = -1;
      }
      if (kind === "up" || kind === "cancel") {
        if (kind === "cancel") {
          ed.cancelStroke();
          this.cancelDrag();
        } else if (this.dragFrom) {
          const from = this.dragFrom,
            to = this.dragTo ?? from;
          ed.clearPreview();
          ed.start(from.x, from.y, this.options(), Date.now());
          ed.move(to.x, to.y, Date.now() + 1);
          ed.end();
          this.cancelDrag();
        } else ed.end();
        this.pointerId = null;
        this.sliding = false;
        this.sizeSliding = false;
        this.painted = -1;
      }
      return true;
    }
    if (this.page !== "battle") return false;
    const s = this.state();
    if (!s || s.phase !== "playing" || this.paused || this.room?.paused)
      return false;
    if (kind === "down") {
      if (
        this.host.paint.hits.some(
          (h) =>
            point.x >= h.x &&
            point.x <= h.x + h.w &&
            point.y >= h.y &&
            point.y <= h.y + h.h,
        )
      )
        return false;
      if (this.pointerId !== null) return true;
      this.pointerId = point.id;
      this.lastPointer = point;
      const me = s.pilots.find((p) => p.id === this.host.player?.id)!;
      if(!me||me.hp<=0){this.pointerId=null;this.lastPointer=undefined;return true;}
      this.target = { ...(this.predicted ?? { x: me.x, y: me.y }) };
      return true;
    }
    if (point.id !== this.pointerId) return false;
    if ((kind === "move" || kind === "up") && this.lastPointer && this.target) {
      this.target.x = Math.max(
        28,
        Math.min(
          worldWidth(s) - 28,
          this.target.x +
            worldDragDelta(
              (point.x - this.lastPointer.x) / this.battleBox.scale,
              worldWidth(s),
            ),
        ),
      );
      this.target.y = Math.max(
        80,
        Math.min(
          1200,
          this.target.y + (point.y - this.lastPointer.y) / this.battleBox.scale,
        ),
      );
      this.lastPointer = point;
      this.predicted = { ...this.target };
      if (!this.room && kind !== "up")
        this.command({ kind: "move", ...this.target });
    }
    if (kind === "up" || kind === "cancel") {
      this.pointerId = null;
      this.lastPointer = undefined;
      if (this.target) this.command({ kind: "move", ...this.target });
      this.target = undefined;
    }
    return true;
  }
}
